import { useMutation } from '@tanstack/react-query';
import { submitBliksyFeedbackContract, type SubmitAiFeedbackResponse } from '@bliks-io/contracts';
import type { ChatFeedbackHandler, ChatFeedbackSubmission } from '@bliks-io/ui';
import { apiClient } from '../client';
import { toastApiError } from './toast-api-error';

/**
 * Post a thumbs verdict on one Bliksy answer (plan 021 PR-1). Returns the
 * handler `ChatMessages` consumes — `mutateAsync` is observer-stable, so hosts
 * pass it straight through — or `undefined` while there is no session to vote
 * in (a brand-new chat's first answer streams before the session id exists).
 *
 * Deliberately does NOT patch the chat-history cache on success. The chat hook
 * re-seeds its transcript from `historyData` on every cache change, so writing
 * to that cache would replace what is on screen with the last fetched history —
 * deleting a just-streamed turn to record a vote on it. The widget's own
 * optimistic state covers the mounted lifetime, and the history query has
 * `staleTime: 0`, so a remount refetches.
 *
 * The response body is therefore not consumed at all: nothing on screen is
 * reconciled from it, which makes a concurrent vote on a shared Bliksy
 * recording last-writer-wins until the next history read (accepted, plan §6H).
 * `mutateAsync` rejects on failure, which is the widget's rollback signal; the
 * toast is raised here so no host has to re-spell it.
 */
export function useSubmitAiFeedback(
  sessionId: string | undefined
): ChatFeedbackHandler | undefined {
  const { mutateAsync } = useMutation({
    mutationFn: async (vote: ChatFeedbackSubmission): Promise<SubmitAiFeedbackResponse> => {
      const { data } = await apiClient.post<SubmitAiFeedbackResponse>(
        submitBliksyFeedbackContract.path,
        { sessionId, ...vote }
      );
      return data;
    },
    onError: (error) => toastApiError(error, 'chat.feedback.error')
  });

  return sessionId ? mutateAsync : undefined;
}
